import React, { useState, useEffect, useCallback } from "react";
import { Form, Input, Button, Cascader, Divider, Modal, Row, Col, Table, Typography, notification } from "antd";
import './SurveysDamageEconomicLoss.css';
import axios from "axios";
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const SurveysDamageEconomicLoss = (props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isParentEditMode = props.isEditMode || false;

  // Modal state management
  const [modalOptions, setModalOption] = useState({
    loading: false,
    visible: false,
    current: null,
    hasCurrent: false
  });

  const [typeList, setTypeList] = useState([]);
  const [economicLossList, setEconomicLossList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const formItemLayout = {
    labelCol: {
      sm: { span: 4 }
    },
    wrapperCol: {
      sm: { span: 20 }
    }
  };

  const showModal = (record) => {
    if (!isParentEditMode) {
      return;
    }

    let option = { ...modalOptions };

    if (record && record.id) {
      option.current = record;
      option.hasCurrent = true;
      form.setFieldsValue({
        type: [record.typeOfCost], // Use the enum value to match the DB value
        estimatedCost: record.estimatedCost,
        approvedCost: record.approvedCost,
        finalCost: record.finalCost
      });
    } else {
      form.resetFields();
    }
    option.visible = true;
    setModalOption(option);
  };

  // Find and add the type label/name to the economic loss object
  const insertType = (obj) => {
    let newObj = { ...obj };
    let typeInfo = typeList.find(type => type.value === newObj.typeOfCost);

    if (typeInfo) {
      newObj.type = {
        value: typeInfo.value,
        label: typeInfo.label
      };
    }

    return newObj;
  }

  // Convert form data to the Backend object format
  const toEcoLossObj = (record) => {
    let newobj = { ...record };
    newobj.damageId = props.damageId;

    // Handle the type field - the Cascader returns an array
    if (Array.isArray(record.type) && record.type.length > 0) {
      newobj.typeOfCost = record.type[0];
    }

    return newobj;
  }

  // Save record to Backend
  const saveEconomicLoss = (record) => {
    try {
      props.startLoading();
      const ecoLossRecord = toEcoLossObj(record);

      axios
        .post(process.env.REACT_APP_API_ENDPOINT + "/economicloss/save", ecoLossRecord)
        .then(({ data }) => {
          loadEconomicLossList();
          notification.success({
            message: 'Success',
            description: 'Economic loss saved successfully',
            placement: 'bottomRight'
          });
          setTimeout(() => {
            props.stopLoading("success");
          }, 1000);
        })
        .catch(function (error) {
          console.log(error);
          notification.error({
            message: 'Error',
            description: 'Failed to save economic loss',
            placement: 'bottomRight'
          });
          setTimeout(() => {
            props.stopLoading("failure");
          }, 1000);
        });
    } catch (error) {
      console.log(error);
      notification.error({
        message: 'Error',
        description: 'An unexpected error occurred',
        placement: 'bottomRight'
      });
      setTimeout(() => {
        props.stopLoading("failure");
      }, 1000);
    }
  };

  // Delete the selected record from backend
  const removeEconomicLoss = (record) => {
    if (!isParentEditMode) {
      return;
    }

    props.startLoading();
    axios
      .delete(process.env.REACT_APP_API_ENDPOINT + "/economicloss/delete", {
        params: {
          id: record.id
        }
      })
      .then(({ data }) => {
        loadEconomicLossList();
        notification.success({
          message: 'Success',
          description: 'Economic loss deleted successfully',
          placement: 'bottomRight'
        });
        setTimeout(() => {
          props.stopLoading("success");
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to delete economic loss',
          placement: 'bottomRight'
        });
        setTimeout(() => {
          props.stopLoading("failure");
        }, 1000);
      });
  }

  // Handle form submission
  const handleOk = () => {
    form.validateFields()
      .then(values => {
        let option = { ...modalOptions };
        option.loading = true;
        setModalOption(option);

        if (option.hasCurrent) {
          let currentRecord = { ...option.current };
          Object.keys(values).forEach(key => {
            if (values[key] !== undefined) {
              currentRecord[key] = values[key];
            }
          });
          saveEconomicLoss(currentRecord);
        } else {
          saveEconomicLoss(values);
        }

        setModalOption({ loading: false, visible: false, hasCurrent: false, current: null });
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const handleCancel = () => {
    setModalOption({ loading: false, visible: false, hasCurrent: false, current: null });
  };

  // Define table columns
  const economicsLossColumns = [
    {
      title: "Type",
      dataIndex: ["type", "label"],
      render: (text, record) => {
        if (record.type && record.type.label) {
          return record.type.label;
        } else {
          const typeInfo = typeList.find(type => type.value === record.typeOfCost);
          return typeInfo ? typeInfo.label : '';
        }
      }
    },
    {
      title: "Estimated Cost",
      dataIndex: "estimatedCost",
      render: value => value ? `€${value}` : ''
    },
    {
      title: "Approved Cost",
      dataIndex: "approvedCost",
      render: value => value ? `€${value}` : ''
    },
    {
      title: "Final Cost",
      dataIndex: "finalCost",
      render: value => value ? `€${value}` : ''
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <span>
          <Button
            type="default"
            size="small"
            disabled={!isParentEditMode}
            onClick={() => showModal(record)}
          >
            Edit
          </Button>
          <Divider type="vertical" />
          <Button
            type="default"
            size="small"
            disabled={!isParentEditMode}
            onClick={() => removeEconomicLoss(record)}
          >
            Delete
          </Button>
        </span>
      )
    }
  ];

  // Load type list from backend
  const loadTypeList = useCallback(() => {
    console.log("Downloading typeList from DB");
    setIsLoading(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/economicloss/enums/cost-types")
      .then(({ data }) => {
        // Map enum names to list of {value, label} objects
        const types = data.map(name => {
          return { value: name, label: t('economicLossEnums.typeOfCost.' + name, name) };
        });
        setTypeList(types);
      })
      .catch(function (error) {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to load economic loss types',
          placement: 'bottomRight'
        });
      })
      .finally(function () {
        setIsLoading(false);
      });
  }, [t]);

  // Load economic loss list for the current damage
  const loadEconomicLossList = useCallback(() => {
    if (!props.damageId || props.damageId === -1) {
      setEconomicLossList([]);
      return;
    }

    console.log("Downloading EconomicLoss list from DB");
    setIsLoading(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/economicloss/", {
        params: { damageId: props.damageId }
      })
      .then(({ data }) => {
        // Process and enrich the data with type information
        const enrichedData = data.map(item => insertType(item));
        setEconomicLossList(enrichedData);
      })
      .catch(function (error) {
        console.log(error);
        notification.error({
          message: 'Error',
          description: 'Failed to load economic losses',
          placement: 'bottomRight'
        });
      })
      .finally(function () {
        setIsLoading(false);
      });
  }, [props.damageId, typeList]);

  // Load type list on component mount
  useEffect(() => {
    loadTypeList();
  }, [loadTypeList]);

  // Load economic loss list when damage ID or type list changes
  useEffect(() => {
    loadEconomicLossList();
  }, [loadEconomicLossList]);

  // Handle form value changes
  const onValuesChange = (changedValues) => {
    form.validateFields(Object.keys(changedValues));
  };

  // Modal form component
  const modalForm = () => {
    return (
      <Modal
        centered
        open={modalOptions.visible}
        title={modalOptions.hasCurrent ? "Edit Economic Loss" : "Add New Economic Loss"}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={modalOptions.loading} onClick={handleOk}>
            Save
          </Button>,
        ]}
      >
        <Form
          form={form}
          {...formItemLayout}
          className="details_form"
          onValuesChange={onValuesChange}
        >
          <Form.Item
            label="Type"
            name="type"
            rules={[{ type: "array", required: true, message: "Please select type!" }]}
          >
            <Cascader
              options={typeList}
              placeholder="Choose loss type"
            />
          </Form.Item>
          <Form.Item
            label="Estimated Cost"
            name="estimatedCost"
            rules={[{ required: false, message: "Please input estimated cost!" }]}
          >
            <Input placeholder="Estimated cost" suffix="€" />
          </Form.Item>
          <Form.Item
            label="Approved Cost"
            name="approvedCost"
            rules={[{ required: false, message: "Please input approved cost!" }]}
          >
            <Input placeholder="Approved cost" suffix="€" />
          </Form.Item>
          <Form.Item
            label="Final Cost"
            name="finalCost"
            rules={[{ required: false, message: "Please input final cost!" }]}
          >
            <Input placeholder="Final cost" suffix="€" />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>Economic Losses</Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => showModal()}
            disabled={!props.damageId || props.damageId === -1 || !isParentEditMode}
          >
            Add New Economic Loss
          </Button>
        </Col>
      </Row>
      <Table
        size="small"
        columns={economicsLossColumns}
        dataSource={economicLossList}
        rowKey={record => record.id}
        loading={isLoading}
        pagination={{ pageSize: 5 }}
      />
      {modalForm()}
    </div>
  );
};

export default SurveysDamageEconomicLoss;